import React from 'react'
import {
  Create,
  SimpleForm,
  FileInput,
  FileField,
  required,
  Toolbar,
  SaveButton,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin'
import {
  Box,
  Button,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  IconButton,
  Alert,
  AlertTitle,
} from '@mui/material'
import {
  MergeType as MergeIcon,
  InsertDriveFile as SingleFileIcon,
  ArrowUpward,
  ArrowDownward,
  Delete as DeleteIcon,
  DragIndicator,
} from '@mui/icons-material'
import { useFormContext, useWatch } from 'react-hook-form'

const MAX_CONTRACT_FILES = 400
const MAX_TOTAL_UPLOAD_BYTES = 450 * 1024 * 1024

const validateContractFiles = (files) => {
  if (!files || files.length === 0) {
    return 'Please select at least one PDF'
  }
  if (files.length > MAX_CONTRACT_FILES) {
    return `Too many files selected. Maximum is ${MAX_CONTRACT_FILES}.`
  }
  const totalBytes = files.reduce(
    (sum, file) => sum + (file?.rawFile?.size || file?.size || 0),
    0
  )
  if (totalBytes > MAX_TOTAL_UPLOAD_BYTES) {
    return `Total upload is too large. Maximum is ${Math.floor(
      MAX_TOTAL_UPLOAD_BYTES / (1024 * 1024)
    )}MB.`
  }
  return undefined
}

const normalizeFiles = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value
  return [value]
}

const getTotalBytes = (files) =>
  normalizeFiles(files).reduce(
    (sum, file) => sum + (file?.rawFile?.size || file?.size || 0),
    0
  )

const UploadModeSelector = () => {
  const { setValue } = useFormContext()
  const uploadMode = useWatch({ name: 'uploadMode' }) || 'single'

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setValue('uploadMode', newMode)
      // Clear files when switching modes
      setValue('files', [], { shouldValidate: true })
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        Upload Mode
      </Typography>
      <ToggleButtonGroup
        value={uploadMode}
        exclusive
        onChange={handleModeChange}
        aria-label="upload mode"
        fullWidth
        sx={{ mb: 1 }}
      >
        <ToggleButton value="single" aria-label="single file upload">
          <SingleFileIcon sx={{ mr: 1 }} />
          Upload Separate Contracts
        </ToggleButton>
        <ToggleButton value="merge" aria-label="merge files">
          <MergeIcon sx={{ mr: 1 }} />
          Upload & Merge as One Contract
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {uploadMode === 'merge'
          ? 'Selected PDFs will be merged into a single contract document. You can reorder files before upload.'
          : 'Each PDF will be uploaded as a separate contract record.'}
      </Typography>
    </Box>
  )
}

const FileReorderList = () => {
  const { setValue } = useFormContext()
  const files = normalizeFiles(useWatch({ name: 'files' }))
  const uploadMode = useWatch({ name: 'uploadMode' }) || 'single'
  const [draggedIndex, setDraggedIndex] = React.useState(null)
  const [dragOverIndex, setDragOverIndex] = React.useState(null)

  if (uploadMode !== 'merge' || files.length === 0) {
    return null
  }

  const moveFile = (index, direction) => {
    const newFiles = [...files]
    const newIndex = direction === 'up' ? index - 1 : index + 1

    if (newIndex < 0 || newIndex >= newFiles.length) return

    const temp = newFiles[index]
    newFiles[index] = newFiles[newIndex]
    newFiles[newIndex] = temp

    setValue('files', newFiles, { shouldValidate: true })
  }

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index)
    setValue('files', newFiles, { shouldValidate: true })
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.parentNode)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const newFiles = [...files]
    const draggedFile = newFiles[draggedIndex]

    // Remove from old position
    newFiles.splice(draggedIndex, 1)
    // Insert at new position
    newFiles.splice(dropIndex, 0, draggedFile)

    setValue('files', newFiles, { shouldValidate: true })
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Merge Order (drag to reorder)
      </Typography>
      <Paper variant="outlined" sx={{ p: 1 }}>
        {files.map((file, index) => (
          <Box
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              mb: index < files.length - 1 ? 1 : 0,
              backgroundColor:
                draggedIndex === index
                  ? '#e3f2fd'
                  : dragOverIndex === index
                  ? '#fff3e0'
                  : '#f5f5f5',
              borderRadius: 1,
              cursor: 'grab',
              transition: 'background-color 0.2s',
              border:
                dragOverIndex === index
                  ? '2px dashed #ff9800'
                  : '2px solid transparent',
              '&:active': {
                cursor: 'grabbing',
              },
              '&:hover': {
                backgroundColor: draggedIndex === index ? '#e3f2fd' : '#eeeeee',
              },
            }}
          >
            <DragIndicator
              fontSize="small"
              sx={{ color: 'text.secondary', cursor: 'grab' }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, minWidth: '24px' }}
            >
              {index + 1}.
            </Typography>
            <Typography
              variant="body2"
              sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {file.title || file.rawFile?.name || 'Unknown file'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => moveFile(index, 'up')}
              disabled={index === 0}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveFile(index, 'down')}
              disabled={index === files.length - 1}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => removeFile(index)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Paper>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        Files will be merged in this order. The first file will be page 1 of the
        merged document.
      </Typography>
    </Box>
  )
}

const SelectionSummary = ({ dropFeedback, onClearSelection }) => {
  const { setValue } = useFormContext()
  const files = normalizeFiles(useWatch({ name: 'files' }))
  const uploadMode = useWatch({ name: 'uploadMode' }) || 'single'
  const count = files.length
  const totalBytes = getTotalBytes(files)
  const totalMb = Math.ceil(totalBytes / (1024 * 1024))
  const hasFiles = count > 0

  return (
    <Box sx={{ mt: 1 }}>
      {uploadMode === 'merge' && hasFiles && count < 2 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <AlertTitle>At least 2 files required for merging</AlertTitle>
          Please select at least 2 PDF files to merge them into one contract.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        {hasFiles
          ? `${count} file${count === 1 ? '' : 's'} selected (${totalMb}MB)`
          : 'No files selected. Drop PDFs into the upload area.'}
      </Typography>
      {dropFeedback && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {dropFeedback}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ mt: 0.5 }}
        onClick={() => {
          setValue('files', [], { shouldValidate: true })
          onClearSelection()
        }}
        disabled={!hasFiles}
      >
        Clear Selection
      </Button>
    </Box>
  )
}

const ContractCreateToolbar = () => {
  const files = normalizeFiles(useWatch({ name: 'files' }))
  const uploadMode = useWatch({ name: 'uploadMode' }) || 'single'
  const count = files.length
  const totalBytes = getTotalBytes(files)

  const hasValidSelection =
    count > 0 &&
    count <= MAX_CONTRACT_FILES &&
    totalBytes <= MAX_TOTAL_UPLOAD_BYTES &&
    (uploadMode === 'single' || count >= 2) // Merge mode requires at least 2 files

  return (
    <Toolbar>
      <SaveButton
        disabled={!hasValidSelection}
        label={uploadMode === 'merge' ? 'Merge & Upload' : 'Upload'}
      />
    </Toolbar>
  )
}

const UploadProgressNotice = () => {
  const [progress, setProgress] = React.useState({
    active: false,
    completed: 0,
    total: 0,
  })

  React.useEffect(() => {
    const handleProgress = (event) => {
      const { detail } = event
      if (!detail) return
      setProgress({
        active: Boolean(detail.active),
        completed: detail.completed || 0,
        total: detail.total || 0,
      })
    }

    window.addEventListener('contracts-upload-progress', handleProgress)
    return () =>
      window.removeEventListener('contracts-upload-progress', handleProgress)
  }, [])

  if (!progress.active) return null

  const pct =
    progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1.5,
        px: 1.5,
        py: 1,
        border: '1px solid #dbeafe',
        borderRadius: 1.5,
        backgroundColor: '#eff6ff',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Uploading contracts: batch {progress.completed}/{progress.total} ({pct}
        %)
      </Typography>
      <Box
        sx={{
          mt: 1,
          width: '100%',
          height: 8,
          borderRadius: 999,
          backgroundColor: '#bfdbfe',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: '#2563eb',
            transition: 'width 200ms linear',
          }}
        />
      </Box>
    </Box>
  )
}

export const ContractCreate = () => {
  const [dropFeedback, setDropFeedback] = React.useState('')

  return (
    <Create
      redirect="list"
      transform={(data) => {
        // Add uploadMode to the form data
        return {
          ...data,
          uploadMode: data.uploadMode || 'single',
        }
      }}
    >
      <SimpleForm toolbar={<ContractCreateToolbar />}>
        <UploadModeSelector />

        <FileInput
          source="files"
          label="Contract PDF(s)"
          accept="application/pdf"
          validate={[required(), validateContractFiles]}
          multiple
          options={{
            maxFiles: MAX_CONTRACT_FILES,
            onDropAccepted: () => setDropFeedback(''),
            onDropRejected: (rejections) => {
              if (!rejections || rejections.length === 0) {
                setDropFeedback('Some files could not be added.')
                return
              }
              const tooMany = rejections.some((r) =>
                (r.errors || []).some((e) => e.code === 'too-many-files')
              )
              const invalidType = rejections.some((r) =>
                (r.errors || []).some((e) => e.code === 'file-invalid-type')
              )
              if (tooMany) {
                setDropFeedback(
                  `Too many files dropped. Maximum is ${MAX_CONTRACT_FILES}.`
                )
                return
              }
              if (invalidType) {
                setDropFeedback('Only PDF files are accepted.')
                return
              }
              setDropFeedback(
                `${rejections.length} file(s) were rejected by the uploader.`
              )
            },
          }}
          helperText={`Max ${MAX_CONTRACT_FILES} PDFs, total ${Math.floor(
            MAX_TOTAL_UPLOAD_BYTES / (1024 * 1024)
          )}MB. Large uploads are automatically batched.`}
          sx={{
            '& .RaFileInput-previews': {
              maxHeight: 220,
              overflowY: 'auto',
            },
          }}
        >
          <FileField source="src" title="title" />
        </FileInput>

        <FileReorderList />

        <SelectionSummary
          dropFeedback={dropFeedback}
          onClearSelection={() => setDropFeedback('')}
        />
        <UploadProgressNotice />
      </SimpleForm>
    </Create>
  )
}
